/* ml-product-video.js — rev 3 (2026-07-29, Me + Lia)

   WHAT THIS IS
   ------------
   The PDP video controller. This logic previously lived at the bottom of
   assets/global.js (a Dawn CORE file) as an IIFE commented "PRODUCT VIDEO FIX".
   It has been moved here so a future Dawn upgrade cannot silently delete it,
   and so it lives next to the rest of the Me + Lia product-page code.

   WHAT CHANGED FROM THE global.js VERSION
   ---------------------------------------
   The old version called loadContent(false) on EVERY product video at script
   parse time. That injected the <video> into the DOM on page load and set
   preload="auto" + autoplay, so the browser downloaded the whole file (2-3MB)
   before the visitor had scrolled anywhere near it. It also ran a
   MutationObserver over document.documentElement with subtree:true, re-running
   the full sweep on every DOM mutation anywhere on the page.

   Now:
   - Nothing loads on page load.
   - An IntersectionObserver activates a video when its slide comes into view
     (200px rootMargin), which covers swipe as well as thumbnail taps. Dawn's
     own media-gallery.js already calls loadContent(false) on thumbnail click
     and variant change, and the loadContent patch below catches those too.
   - The grey-poster problem the original was written to solve is unchanged:
     .ml-video-ready is still applied at the moment the video loads, which is
     the only moment it matters.
   - Videos pause when scrolled out of view.
   - The MutationObserver is scoped to the gallery and debounced.

   Note: loading="lazy" does nothing on <video> — it is only defined for <img>
   and <iframe>. `preload` is the real lever, and it is set to 'none' in
   snippets/product-thumbnail.liquid.
*/
(function () {
  'use strict';

  var READY_CLASS = 'ml-video-ready';
  var CONTAINER_SELECTOR = '.product-media-container.media-type-video';

  function isProductPage() {
    return !!(document.body && document.body.classList.contains('template-product'));
  }

  // Only ever touch video containers — never image media, which would break clickability.
  function isProductVideo(dm) {
    return !!(dm && isProductPage() && dm.closest(CONTAINER_SELECTOR));
  }

  function markReady(dm) {
    if (dm) dm.classList.add(READY_CLASS);
  }

  function tryPlay(video) {
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }

  function configureHtml5Video(dm) {
    var video = dm.querySelector('video');
    if (!video) return;

    // Required for inline autoplay on iOS / Safari
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');

    video.controls = false;
    video.removeAttribute('controls');

    // Hide the grey poster only now, when the video is actually loading
    markReady(dm);

    tryPlay(video);
    ['loadedmetadata', 'canplay', 'loadeddata'].forEach(function (ev) {
      video.addEventListener(ev, function () { tryPlay(video); }, { once: true });
    });
  }

  function activate(dm) {
    if (!isProductVideo(dm)) return;
    if (!dm.hasAttribute('loaded') && typeof dm.loadContent === 'function') {
      // focus=false prevents focus jumps; the patch below configures the video
      dm.loadContent(false);
    } else {
      configureHtml5Video(dm);
    }
  }

  function deactivate(dm) {
    var video = dm && dm.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  // Patch Dawn's deferred-media loader so ANY load path (thumbnail click, arrow,
  // variant change via media-gallery.js) configures the video for autoplay.
  function patchDeferredMediaPrototype() {
    var El = customElements.get('deferred-media');
    if (!El || !El.prototype) return;

    var proto = El.prototype;
    if (proto.__mlVideoPatched) return;
    proto.__mlVideoPatched = true;

    var originalLoadContent = proto.loadContent;

    proto.loadContent = function (focus) {
      if (focus === undefined) focus = true;
      originalLoadContent.call(this, focus);
      if (isProductVideo(this)) configureHtml5Video(this);
    };
  }

  var io = null;

  function observeVideoContainers() {
    if (!isProductPage()) return;

    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var dm = entry.target.querySelector('deferred-media');
            if (!dm) return;
            if (entry.isIntersecting) {
              activate(dm);
            } else {
              deactivate(dm);
            }
          });
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      );
    }

    document.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
      if (container.dataset.mlVideoObserved) return;
      container.dataset.mlVideoObserved = '1';
      io.observe(container);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function init() {
    if (!isProductPage()) return;

    if (customElements.get('deferred-media')) {
      patchDeferredMediaPrototype();
    } else {
      customElements.whenDefined('deferred-media').then(patchDeferredMediaPrototype);
    }

    observeVideoContainers();

    // Re-observe if the gallery rerenders (variant change rebuilds the media set).
    // Scoped to the gallery, not documentElement, and debounced.
    var gallery =
      document.querySelector('media-gallery') ||
      document.querySelector('.product__media-wrapper') ||
      document.querySelector('.product__media-list');

    if (gallery) {
      var mo = new MutationObserver(debounce(observeVideoContainers, 200));
      mo.observe(gallery, { childList: true, subtree: true });
    }

    // Safety: if someone taps the poster directly, load it straight away.
    document.addEventListener(
      'pointerdown',
      function (e) {
        var dm = e.target.closest && e.target.closest('deferred-media');
        if (!dm || !isProductVideo(dm)) return;
        activate(dm);
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
